export const ACC_URL = import.meta.env.VITE_ACC_URL || 'https://00acc.com';
export const CDN_URL = import.meta.env.VITE_CDN_URL || 'https://cdn.00team.com';

// Pay server origin (strip /api/v1 from VITE_API_BASE_URL). Used for non-API
// endpoints that live at the pay server root — /session/:id/status,
// /widgets/card/:id, /widgets/applepay/:id.
export const PAY_URL: string = (() => {
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!base) return '';
  try { return new URL(base).origin; } catch { return base.replace(/\/api\/v1\/?$/, ''); }
})();

export const MEMBERSHIP_PLANS: Record<string, { price: number; priceYearly: number; titleAr: string; titleEn: string }> = {
  pro: { price: 29, priceYearly: 290, titleAr: 'باقة نمو', titleEn: 'Growth Plan' },
  business: { price: 170, priceYearly: 1700, titleAr: 'باقة احتراف', titleEn: 'Pro Plan' },
};

export const ORDER_STATUSES = ['pending', 'processing', 'completed', 'cancelled', 'refunded', 'failed'] as const;
export const DELIVERY_STATUSES = ['confirming', 'confirmed', 'delivering', 'delivered', 'returned', 'cancelled'] as const;

// Tailwind classes per delivery status. Static literals so JIT picks them up.
// Pairs with `statusVariants` in StatusBadge.tsx. Shared by the orders table
// and the dashboard home widget so both read the same at a glance.
export const DELIVERY_STATUS_CLASSES: Record<string, string> = {
  confirming: 'bg-warning/15 text-warning border-warning/40',
  confirmed:  'bg-info/15 text-info border-info/40',
  delivering: 'bg-accent/15 text-accent border-accent/40',
  delivered:  'bg-success/15 text-success border-success/40',
  returned:   'bg-danger/15 text-danger border-danger/40',
  cancelled:  'bg-danger/15 text-danger border-danger/40',
};
export const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'refunded', 'partially_refunded'] as const;
export const SHIPPING_STATUSES = ['not_shipped', 'pending', 'shipped', 'in_transit', 'delivered', 'failed'] as const;
export const PRODUCT_TYPES = ['physical', 'digital', 'code', 'file', 'service'] as const;
export const CURRENCIES = ['SAR', 'USD', 'EUR', 'GBP', 'AED', 'KWD', 'BHD', 'OMR', 'QAR', 'TRY', 'EGP', 'LYD'] as const;
export const LANGUAGES = [
  'ar', 'en', 'fr', 'es', 'de', 'tr', 'ur', 'fa', 'hi', 'id',
  'ms', 'pt', 'it', 'nl', 'ru', 'zh', 'ja', 'ko', 'th', 'vi',
] as const;

export const LANGUAGE_NAMES: Record<string, { english: string; native: string }> = {
  ar: { english: 'Arabic', native: 'العربية' },
  en: { english: 'English', native: 'English' },
  fr: { english: 'French', native: 'Français' },
  es: { english: 'Spanish', native: 'Español' },
  de: { english: 'German', native: 'Deutsch' },
  tr: { english: 'Turkish', native: 'Türkçe' },
  ur: { english: 'Urdu', native: 'اردو' },
  fa: { english: 'Persian', native: 'فارسی' },
  hi: { english: 'Hindi', native: 'हिन्दी' },
  id: { english: 'Indonesian', native: 'Bahasa Indonesia' },
  ms: { english: 'Malay', native: 'Bahasa Melayu' },
  pt: { english: 'Portuguese', native: 'Português' },
  it: { english: 'Italian', native: 'Italiano' },
  nl: { english: 'Dutch', native: 'Nederlands' },
  ru: { english: 'Russian', native: 'Русский' },
  zh: { english: 'Chinese', native: '中文' },
  ja: { english: 'Japanese', native: '日本語' },
  ko: { english: 'Korean', native: '한국어' },
  th: { english: 'Thai', native: 'ไทย' },
  vi: { english: 'Vietnamese', native: 'Tiếng Việt' },
};

/** Format a language code for display: "Native (English)" or just the code if unknown */
export function getLanguageLabel(code: string): string {
  const names = LANGUAGE_NAMES[code];
  if (!names) return code;
  if (names.english === names.native) return names.english;
  return `${names.native} (${names.english})`;
}

export const USER_ROLES = ['owner', 'admin', 'manager', 'staff', 'viewer'] as const;

export const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-warning/20 text-warning',
  processing: 'bg-info/20 text-info',
  completed: 'bg-success/20 text-success',
  cancelled: 'bg-danger/20 text-danger',
  refunded: 'bg-text-muted/20 text-text-muted',
  failed: 'bg-danger/20 text-danger',
  paid: 'bg-success/20 text-success',
  partially_refunded: 'bg-warning/20 text-warning',
  not_shipped: 'bg-text-muted/20 text-text-muted',
  shipped: 'bg-info/20 text-info',
  in_transit: 'bg-accent/20 text-accent',
  delivered: 'bg-success/20 text-success',
  // Legacy delivery statuses
  confirming: 'bg-warning/20 text-warning',
  confirmed: 'bg-info/20 text-info',
  delivering: 'bg-info/20 text-info',
  returned: 'bg-danger/20 text-danger',
};

export const PAGE_SIZES = [10, 20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 50;
